import React, { useState, useCallback, useEffect, useMemo } from 'react';
import ReactFlow, {
  Background,
  Controls,
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  NodeChange,
  EdgeChange,
  Edge,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Plus, Save, ArrowLeft, Loader2, Trash2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Combobox } from '@/components/ui/combobox';
import { SiteHeader } from '@/components/site-header';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import { FadeId } from '@/components/ui/fade-id';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import AssistantNode from './AssistantNode';
import { getAssistants } from '@/api/assistants/assistants.api';
import { Assistant } from '@/api/assistants/assistants.types';
import { CreateMultiAgentRequest, MultiAgentMember } from '@/api/multi-agent/multi_agent.types';
import { createMultiAgent, getMultiAgentById, updateMultiAgent, deleteMultiAgent } from '@/api/multi-agent/multi_agent.api';
import { Skeleton } from '@/components/ui/skeleton';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';

const GRID_SPACING_X = 350; // Increased horizontal spacing for better connection visibility
const GRID_SPACING_Y = 200; // Increased vertical spacing to prevent connection overlap
const INITIAL_X = 100;  // Starting X position
const INITIAL_Y = 100;  // Starting Y position

const calculateNodePositions = (members: MultiAgentMember[], edges: Edge[]) => {
  const nodePositions = new Map<string, { x: number, y: number }>();
  const nodeLevels = new Map<string, number>();
  const nodesInLevel = new Map<number, number>();
  
  // First pass: Find starting nodes (nodes with no incoming edges)
  const hasIncomingEdges = new Set<string>();
  edges.forEach(edge => hasIncomingEdges.add(edge.target));
  
  const startNodes = members
    .filter(member => !hasIncomingEdges.has(member.assistantId))
    .map(member => member.assistantId);

  // Second pass: Assign levels through BFS
  const queue = startNodes.map(id => ({ id, level: 0 }));
  while (queue.length > 0) {
    const { id, level } = queue.shift()!;
    if (nodeLevels.has(id)) continue;
    
    nodeLevels.set(id, level);
    nodesInLevel.set(level, (nodesInLevel.get(level) || 0) + 1);
    
    // Add children to queue
    edges
      .filter(edge => edge.source === id)
      .forEach(edge => {
        queue.push({ id: edge.target, level: level + 1 });
      });
  }

  // If there are nodes not assigned levels (due to cycles), assign them to level 0
  members.forEach(member => {
    if (!nodeLevels.has(member.assistantId)) {
      const level = 0;
      nodeLevels.set(member.assistantId, level);
      nodesInLevel.set(level, (nodesInLevel.get(level) || 0) + 1);
    }
  });

  // Third pass: Position nodes
  const levelPositions = new Map<number, number>(); // Tracks current Y position for each level
  
  nodeLevels.forEach((level, nodeId) => {
    const currentPosition = levelPositions.get(level) || 0;
    
    // Calculate position
    const x = INITIAL_X + (level * GRID_SPACING_X);
    const y = INITIAL_Y + (currentPosition * GRID_SPACING_Y);
    
    nodePositions.set(nodeId, { x, y });
    levelPositions.set(level, currentPosition + 1);
  });

  return nodePositions;
};

const MultiAgentWorkflowBuilder: React.FC = () => {
  const navigate = useNavigate();
  const { error, success } = useEnhancedToast();
  const { user } = useAuth();
  const { id } = useParams();
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [isLoadingAssistants, setIsLoadingAssistants] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [multiAgentName, setMultiAgentName] = useState('');
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [nodeCounter, setNodeCounter] = useState(0);
  const [isLoading, setIsLoading] = useState(!!id);

  const canEditAgents = user?.permissions?.multi_agent === "EDITOR";

  const canSave = useMemo(() => {
    return multiAgentName.trim() !== '' && nodes.length > 0;
  }, [multiAgentName, nodes]);

  const handleClose = () => {
    navigate('/dashboard/multi-agent');
  };

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const templateId = searchParams.get('template');

  useEffect(() => {
    loadAssistants();
  }, []);

  useEffect(() => {
    // Only load multi agent after assistants are loaded
    // console.log('Checking conditions to load multi-agent:', { id, assistantsLength: assistants.length, isLoadingAssistants }); // Keep log commented out for potential future debug
    // We should attempt to load the multi-agent even if the assistants list is empty, 
    // as loadMultiAgent handles missing/deleted assistants internally.
    if (id && !isLoadingAssistants) {
      // console.log('Conditions met, calling loadMultiAgent'); // Keep log commented out
      loadMultiAgent();
    }
  }, [id, assistants, isLoadingAssistants]);

  const loadAssistants = async () => {
    try {
      setIsLoadingAssistants(true);
      const response = await getAssistants();
      if (response.error) {
        throw new Error(response.error);
      }
      setAssistants(response.data || []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "An unknown error occurred";
      error({
        icon: AlertCircle,
        title: "Failed to Load Assistants",
        description: message || "Could not load assistants. Please try again.",
        duration: 4000,
      });
      // Removed console.error for production
    } finally {
      setIsLoadingAssistants(false);
    }
  };

  const loadMultiAgent = async () => {
    try {
      // console.log('ENTERED loadMultiAgent TRY block'); // Removed log
      setIsLoading(true);
      
      const response = await getMultiAgentById(id!);
      if (response.error) {
        throw new Error(response.error);
      }

      // Set the name
      setMultiAgentName(response.data.name);

      // Create edges first so we can use them for positioning
      const newEdges: Edge[] = [];
      response.data.members?.forEach(member => {
        member.assistantDestinations?.forEach(dest => {
          if (dest.assistantName) {
            // Find the target assistant ID from the name
            const targetAssistant = assistants.find(a => a.name === dest.assistantName);
            if (targetAssistant) {
              newEdges.push({
                id: `${member.assistantId}-${targetAssistant.id}`,
                source: member.assistantId,
                target: targetAssistant.id,
                type: 'smoothstep',
              });
            }
          }
        });
      });

      // Calculate optimal positions for nodes
      const nodePositions = calculateNodePositions(response.data.members || [], newEdges);
      
      // Create nodes with calculated positions
      const mappedNodes = response.data.members?.map((member, index) => {
        const assistant = assistants.find(a => a.id === member.assistantId);
        const position = nodePositions.get(member.assistantId) || { x: 0, y: 0 };
        
        return {
          id: member.assistantId,
          type: 'assistant',
          position,
          data: {
            label: assistant?.name || 'Deleted Assistant',
            assistant,
            isFirst: index === 0,
            isDeleted: !assistant,
            assistantId: member.assistantId,
            voiceName: assistant?.voice?.voiceId,
            language: assistant?.transcriber?.language,
            maxDurationSeconds: assistant?.maxDurationSeconds,
            knowledgeBaseFiles: assistant?.model?.knowledgeBaseId,
            recordingEnabled: assistant?.artifactPlan?.recordingEnabled,
            firstMessageMode: assistant?.firstMessageMode,
            onDelete: () => handleNodeDelete(member.assistantId),
            destinations: member.assistantDestinations?.map(dest => ({
              type: "assistant",
              assistantName: dest.assistantName || '',
              message: dest.message || '',
              description: dest.description || '',
              transferMode: dest.transferMode || 'rolling-history',
            })) || [],
            onDestinationSettingsChange: (settings) => handleDestinationSettingsChange(member.assistantId, settings),
          }
        };
      }).filter(Boolean) || [];

      setNodes(mappedNodes);
      setEdges(newEdges);

    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "An unknown error occurred fetching the Multi Agent";
      // console.error('Error loading Multi Agent:', err); // Keep commented out for potential future debug
      error({
        icon: AlertCircle,
        title: "Multi Agent Not Found",
        description: message || "The requested Multi Agent does not exist or you don't have permission to view it.",
        duration: 4000,
      });
      // Navigate back to the list page
      navigate('/dashboard/multi-agent');
    } finally {
      // console.log('Setting isLoading to false'); // Removed log
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!canSave) return;

    try {
      setIsSaving(true);

      // Convert nodes and edges to the API format
      const members = nodes.map(node => {
        // Find all edges where this node is the source
        const nodeEdges = edges.filter(edge => edge.source === node.id);
        
        // Get all valid destinations based on existing edges
        const validDestinations = nodeEdges.map(edge => {
          const targetNode = nodes.find(n => n.id === edge.target);
          if (!targetNode) return null;

          // Find existing destination settings for this target
          const existingDestination = node.data.destinations?.find(
            d => d.assistantName === targetNode.data.label
          );

          // Return either existing settings or default settings
          return {
            type: "assistant",
            assistantName: targetNode.data.label,
            message: existingDestination?.message || '',
            description: existingDestination?.description || '',
            transferMode: existingDestination?.transferMode || 'rolling-history',
          };
        }).filter(Boolean);

        // console.log('Valid destinations for node:', node.id, validDestinations);
        
        const member: MultiAgentMember = {
          assistantId: node.id,
          assistantDestinations: validDestinations,
        };

        return member;
      });

      const request: CreateMultiAgentRequest = {
        name: multiAgentName,
        members
      };

    //   console.log('Saving multi agent with request:', request);

      let response;
      if (id) {
        response = await updateMultiAgent(id, request);
      } else {
        response = await createMultiAgent(request);
      }

    //   console.log('Save response:', response);

      if (response.error) {
        throw new Error(response.error);
      }

      success({
        icon: CheckCircle2,
        title: "Multi Agent Saved",
        description: `Multi Agent ${id ? 'updated' : 'created'} successfully`,
        duration: 3000,
      });

      // If this was a new multi agent, update the URL with the new ID
      if (!id && response.data?.id) {
        navigate(`/dashboard/multi-agent/${response.data.id}`, { replace: true });
      }

      // Reload the multi agent data to ensure we have the latest state
      if (response.data?.id) {
        const refreshResponse = await getMultiAgentById(response.data.id);
        // console.log('Refresh response:', refreshResponse);
        
        if (!refreshResponse.error && refreshResponse.data) {
          setMultiAgentName(refreshResponse.data.name);
          
          // Keep track of current edges to maintain valid connections
          const currentEdges = edges;
          
          // Recreate nodes and edges with the latest data
          const newEdges: Edge[] = [];
          refreshResponse.data.members?.forEach(member => {
            member.assistantDestinations?.forEach(dest => {
              if (dest.assistantName) {
                const targetAssistant = assistants.find(a => a.name === dest.assistantName);
                if (targetAssistant) {
                  // Check if this edge already exists in current edges
                  const existingEdge = currentEdges.find(
                    edge => edge.source === member.assistantId && edge.target === targetAssistant.id
                  );

                  newEdges.push({
                    // Preserve existing edge ID if it exists
                    id: existingEdge?.id || `${member.assistantId}-${targetAssistant.id}`,
                    source: member.assistantId,
                    target: targetAssistant.id,
                    type: 'smoothstep',
                  });
                }
              }
            });
          });

          const nodePositions = calculateNodePositions(refreshResponse.data.members || [], newEdges);
          const mappedNodes = refreshResponse.data.members?.map((member, index) => {
            const assistant = assistants.find(a => a.id === member.assistantId);
            if (!assistant) {
            //   console.warn(`Assistant not found for id: ${member.assistantId}`);
              return null;
            }
            
            const position = nodePositions.get(member.assistantId) || { x: 0, y: 0 };
            
            // console.log('Mapping member with destinations:', member.assistantDestinations);
            
            return {
              id: member.assistantId,
              type: 'assistant',
              position,
              data: {
                label: assistant.name,
                assistant,
                isFirst: index === 0,
                isDeleted: !assistant,
                assistantId: member.assistantId,
                voiceName: assistant.voice?.voiceId,
                language: assistant.transcriber?.language,
                maxDurationSeconds: assistant.maxDurationSeconds,
                knowledgeBaseFiles: assistant.model?.knowledgeBaseId,
                recordingEnabled: assistant.artifactPlan?.recordingEnabled,
                firstMessageMode: assistant.firstMessageMode,
                onDelete: () => handleNodeDelete(member.assistantId),
                destinations: member.assistantDestinations?.map(dest => ({
                  type: "assistant",
                  assistantName: dest.assistantName || '',
                  message: dest.message || '',
                  description: dest.description || '',
                  transferMode: dest.transferMode || 'rolling-history',
                })) || [],
                onDestinationSettingsChange: (settings) => handleDestinationSettingsChange(member.assistantId, settings),
              }
            };
          }).filter(Boolean) || [];

        //   console.log('Setting nodes with:', mappedNodes);
          setNodes(mappedNodes);
          // Update edges while preserving existing valid connections
        //   console.log('Setting edges with:', newEdges);
          setEdges(newEdges);
        }
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "An unknown error occurred";
      error({
        icon: AlertCircle,
        title: "Failed to Save Multi Agent",
        description: message || "Could not save multi agent. Please try again.",
        duration: 4000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    
    try {
      setIsDeleting(true);
      const response = await deleteMultiAgent(id);
      
      if (response.error) {
        throw new Error(response.error);
      }

      success({
        icon: Trash2,
        title: "Multi Agent Deleted",
        description: "Multi Agent was successfully deleted",
        duration: 3000,
      });

      navigate('/dashboard/multi-agent');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "An unknown error occurred";
      error({
        icon: AlertCircle,
        title: "Failed to Delete Multi Agent",
        description: message || "Could not delete multi agent. Please try again.",
        duration: 4000,
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const getAvailableAssistants = () => {
    return assistants.filter(assistant => !nodes.some(node => node.id === assistant.id));
  };

  const onConnect = useCallback((connection: Connection) => {
    //   console.log('onConnect called with:', connection);
    
    if (!canEditAgents) {
      error({
        icon: Ban,
        title: "Permission Denied",
        description: "You need editor permissions to modify multi agents.",
        duration: 4000,
      });
      return;
    }

    setEdges((eds) => {
      const newEdges = addEdge(connection, eds);
      
      // Update the source node's destinations
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === connection.source) {
            // Find the target node to get its name
            const targetNode = nds.find(n => n.id === connection.target);
            // console.log('Found target node:', targetNode);
            
            if (!targetNode) {
              // console.warn('Target node not found');
              return node;
            }

            // Keep existing destinations and add the new one
            const existingDestinations = node.data.destinations || [];
            const newDestination = {
              type: "assistant",
              assistantName: targetNode.data.label,
              message: '',
              description: '',
              transferMode: 'rolling-history' as const,
            };

            // Only add if it doesn't exist
            if (!existingDestinations.some(d => d.assistantName === targetNode.data.label)) {
              const updatedNode = {
                ...node,
                data: {
                  ...node.data,
                  destinations: [...existingDestinations, newDestination],
                },
              };
              // console.log('Updated node:', updatedNode);
              return updatedNode;
            }
            return node;
          }
          return node;
        })
      );
      
      return newEdges;
    });
  }, [canEditAgents]);

//   const onNodesDelete = useCallback((deleted: any[]) => {
//     if (!canEditAgents) {
//       error({
//         icon: Ban,
//         title: "Permission Denied",
//         description: "You need editor permissions to modify multi agents.",
//         duration: 4000,
//       });
//       return;
//     }
//     // Rest of the delete logic...
//   }, [canEditAgents]);

  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      // Handle node removal and cleanup destination settings
      changes.forEach(change => {
        if (change.type === 'remove' || (change.type === 'position' && change.dragging === false)) {
          const removedNode = nodes.find(n => n.id === change.id);
          if (removedNode) {
            const removedNodeName = removedNode.data.label;
            // console.log('Node being removed/dragged:', { id: change.id, name: removedNodeName });
            
            // For position changes, check if the node is outside the canvas
            if (change.type === 'position') {
              const position = removedNode.position;
              const isOutsideCanvas = position.x < -1000 || position.y < -1000 || position.x > 2000 || position.y > 2000;
              if (!isOutsideCanvas) return;
            }
            
            // Clean up any destination settings that reference this node
            setNodes(nds =>
              nds.map(node => {
                if (!node.data.destinations?.length) return node;
                
                // console.log('Checking node:', node.id, 'destinations:', node.data.destinations);
                const hasRemovedNodeAsDestination = node.data.destinations.some(
                  dest => dest.assistantName === removedNodeName
                );
                
                if (hasRemovedNodeAsDestination) {
                  // console.log('Found and cleaning up node with removed destination:', node.id);
                  return {
                    ...node,
                    data: {
                      ...node.data,
                      destinations: [],
                    },
                  };
                }
                return node;
              })
            );
          }
        }
      });
      
      // Call the original onNodesChange
      onNodesChange(changes);
    },
    [nodes, onNodesChange]
  );

  const handleEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      // Handle edge removal and cleanup destination settings
      changes.forEach(change => {
        if (change.type === 'remove') {
          const removedEdge = edges.find(edge => edge.id === change.id);
          if (removedEdge) {
            // Clean up destination settings for the source node
            setNodes(nds => 
              nds.map(node => {
                if (node.id === removedEdge.source) {
                  // Find the target node to get its name
                  const targetNode = nds.find(n => n.id === removedEdge.target);
                  if (!targetNode) return node;

                  // console.log('Cleaning up destination settings for node:', node.id);
                  
                  // Get remaining edges for this source node
                  const remainingEdges = edges.filter(
                    edge => edge.source === node.id && edge.id !== change.id
                  );

                  // Get remaining target nodes
                  const remainingTargets = remainingEdges.map(edge => 
                    nds.find(n => n.id === edge.target)
                  ).filter(Boolean);

                  // Filter destinations to keep only those that still have valid connections
                  const updatedDestinations = (node.data.destinations || []).filter(dest => 
                    remainingTargets.some(target => target?.data.label === dest.assistantName)
                  );

                  return {
                    ...node,
                    data: {
                      ...node.data,
                      destinations: updatedDestinations,
                    },
                  };
                }
                return node;
              })
            );
          }
        }
      });
      
      // Call the original onEdgesChange
      onEdgesChange(changes);
    },
    [edges, onEdgesChange, setNodes]
  );

  const handleNodeDelete = useCallback((nodeId: string) => {
    // Find all edges connected to this node
    const connectedEdges = edges.filter(edge => edge.source === nodeId || edge.target === nodeId);
    
    // Clean up destination settings for all affected nodes
    setNodes((nds) => {
      return nds.filter((node) => node.id !== nodeId).map(node => {
        // If this node had an edge to the deleted node, we need to update its destinations
        const wasConnectedToDeletedNode = connectedEdges.some(edge => edge.source === node.id && edge.target === nodeId);
        const otherEdges = edges.filter(edge => 
          edge.source === node.id && 
          edge.target !== nodeId && 
          // Make sure the target node still exists
          nds.some(n => n.id === edge.target && n.id !== nodeId)
        );

        if (wasConnectedToDeletedNode) {
          // Get the target nodes for remaining edges
          const remainingTargets = otherEdges.map(edge => 
            nds.find(n => n.id === edge.target)
          ).filter(Boolean);

          // Keep destinations only for remaining targets
          const updatedDestinations = node.data.destinations?.filter(dest =>
            remainingTargets.some(target => target?.data.label === dest.assistantName)
          ) || [];

          return {
            ...node,
            data: {
              ...node.data,
              destinations: updatedDestinations,
            },
          };
        }
        return node;
      });
    });

    // Remove all edges connected to the deleted node
    setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
  }, [edges]);

  const handleAddAssistant = useCallback((assistantId: string) => {
    const assistant = assistants.find(a => a.id === assistantId);
    if (!assistant) return;

    const existingNodes = nodes.length;
    const position = {
      x: INITIAL_X + (existingNodes * GRID_SPACING_X),
      y: INITIAL_Y
    };

    const newNode = {
      id: assistantId,
      type: 'assistant',
      position,
      data: {
        label: assistant.name,
        assistant,
        isFirst: existingNodes === 0,
        voiceName: assistant.voice?.voiceId,
        language: assistant.transcriber?.language,
        maxDurationSeconds: assistant.maxDurationSeconds,
        knowledgeBaseFiles: assistant.model?.knowledgeBaseId,
        recordingEnabled: assistant.artifactPlan?.recordingEnabled,
        firstMessageMode: assistant.firstMessageMode,
        onDelete: () => handleNodeDelete(assistantId),
        destinations: [],
        onDestinationSettingsChange: (settings) => handleDestinationSettingsChange(assistantId, settings),
      },
    };

    // console.log('Adding new node:', newNode);
    setNodes((nds) => nds.concat(newNode));
    setNodeCounter(existingNodes + 1);
  }, [nodes, assistants]);

  const handleDestinationSettingsChange = useCallback((sourceNodeId: string, settings: {
    assistantName: string;
    message: string;
    description: string;
    transferMode: 'rolling-history' | 'swap-system-message-in-history' | 'delete-history';
  }) => {
    // console.log('Updating settings for node:', sourceNodeId, 'with:', settings);
    
    setNodes((nds) => {
      const updatedNodes = nds.map((node) => {
        if (node.id === sourceNodeId) {
          // Get existing destinations
          const existingDestinations = node.data.destinations || [];
          
          // Remove old settings for this destination if they exist
          const filteredDestinations = existingDestinations.filter(
            d => d.assistantName !== settings.assistantName
          );
          
          // Add the new settings
          const updatedDestinations = [...filteredDestinations, {
            type: "assistant",
            ...settings,
          }];
          
          const updatedNode = {
            ...node,
            data: {
              ...node.data,
              destinations: updatedDestinations,
            },
          };
          // console.log('Node before update:', node);
          // console.log('Node after update:', updatedNode);
          return updatedNode;
        }
        return node;
      });
      // console.log('All nodes after update:', updatedNodes);
      return updatedNodes;
    });
  }, []);

  // Update isOnlyNode when nodes change
  React.useEffect(() => {
    if (nodes.length === 1) {
      setNodes(nds =>
        nds.map(node => ({
          ...node,
          data: { ...node.data, isOnlyNode: true }
        }))
      );
    }
  }, [nodes.length, setNodes]);

  const nodeTypes = useMemo(() => ({
    assistant: (props: any) => (
      <AssistantNode {...props} edges={edges} assistants={assistants} />
    ),
  }), [edges, assistants]);

  return (
    <div className="h-full flex flex-col">
      <SiteHeader
        title={id ? 'Edit Multi Agent' : 'Create Multi Agent'}
        subtitle={id ? 'Modify your multi-agent workflow configuration' : 'Configure your multi-agent workflow and connect assistants'}
        backButton={
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        }
      >
        {/* Name Input */}
        {isLoading ? (
          <Skeleton className="h-9 w-[200px] lg:w-[250px]" />
        ) : (
          <Input
            placeholder="Multi-agent name..."
            value={multiAgentName}
            onChange={(e) => setMultiAgentName(e.target.value)}
            className="h-9 w-[200px] lg:w-[250px]"
            disabled={isLoading}
          />
        )}

        {/* Add Assistant Combobox */}
        {isLoadingAssistants ? (
          <Skeleton className="h-9 w-[300px] lg:w-[350px]" />
        ) : assistants.length === 0 ? (
          <Button variant="outline" disabled size="sm" className="w-[300px] lg:w-[350px]">
            Create Assistant First
          </Button>
        ) : (
          <Combobox
            options={getAvailableAssistants().map(assistant => ({
              label: assistant.name,
              value: assistant.id,
              description: assistant.id
            }))}
            value=""
            onValueChange={(value) => {
              if (value) {
                handleAddAssistant(value);
              }
            }}
            placeholder={getAvailableAssistants().length === 0 ? "All added" : "Add assistant..."}
            emptyMessage="No assistants available"
            searchPlaceholder="Search assistants..."
            disabled={getAvailableAssistants().length === 0}
            className="w-[300px] lg:w-[350px]"
          />
        )}

        {id && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => canEditAgents && setShowDeleteConfirm(true)}
            disabled={!canEditAgents || isDeleting || isSaving}
            className="gap-2 text-destructive hover:text-destructive"
          >
            {isDeleting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4" />
                Delete
              </>
            )}
          </Button>
        )}
        <Button 
          onClick={handleSave} 
          disabled={!canSave || !canEditAgents || isSaving}
          size="sm"
          className="gap-2"
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {id ? 'Saving...' : 'Creating...'}
            </>
          ) : (
            <>
              {id ? (
                <>
                  <Save className="h-4 w-4" />
                  Save
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  Create Multi Agent
                </>
              )}
            </>
          )}
        </Button>
      </SiteHeader>

      <div className="flex-1 overflow-auto">
        <div className="h-full">
          <div style={{ height: '100%' }}>
            {isLoading ? (
              <div className="w-full h-full bg-background/50 flex items-center justify-center">
                <div className="space-y-8 flex flex-col items-center">
                  <div className="p-4 rounded-lg bg-primary/10">
                    <Loader2 className="w-8 h-8 text-primary animate-spin" />
                  </div>
                  <div className="space-y-2 text-center">
                    <div className="h-4 w-48 bg-muted animate-pulse rounded mx-auto" />
                    <div className="h-3 w-64 bg-muted animate-pulse rounded mx-auto" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative h-full">
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={handleNodesChange}
                  onEdgesChange={handleEdgesChange}
                  onConnect={onConnect}
                  nodeTypes={nodeTypes}
                  fitView
                  style={{ height: '100%' }}
                  defaultViewport={{
                    x: 0,
                    y: 0,
                    zoom: 1,
                  }}
                  minZoom={0.2}
                  maxZoom={1.5}
                  className="bg-background/50 border rounded-b-lg overflow-hidden"
                  proOptions={{ hideAttribution: true }}
                  defaultEdgeOptions={{
                    type: 'smoothstep',
                    style: {
                      strokeWidth: 2,
                      stroke: 'hsl(var(--primary))',
                    },
                    className: 'hover:opacity-70 transition-opacity duration-200 cursor-pointer',
                    animated: true
                  }}
                  fitViewOptions={{
                    padding: 0.5,
                    minZoom: 1,
                    maxZoom: 1,
                  }}
                >
                  <Background />
                  <Controls />
                </ReactFlow>

                {/* Floating ID Badge (Edit Mode Only) */}
                {id && (
                  <div className="absolute top-4 right-4 z-10">
                    <FadeId 
                      id={id} 
                      label="Multi-Agent ID"
                      tooltipSide="left"
                      height="h-9"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmationDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Multi Agent"
        description={`Are you sure you want to delete "${multiAgentName || 'this multi agent'}"? This action cannot be undone.`}
        isLoading={isDeleting}
        onConfirm={handleDelete}
        confirmText="Delete"
        variant="destructive"
      />
    </div>
  );
};

export default MultiAgentWorkflowBuilder;
